import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './LanguageSelector.module.css';
import classNames from 'classnames';

const LANGUAGE_LABELS = {
    en: 'English',
    he: 'עברית',
    ar: 'العربية',
    ru: 'Русский',
};

const LANGUAGES = ['en', 'ar', 'he', 'ru'];

export function LanguageSelector({ onChange }) {
    const { i18n } = useTranslation();
    const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem('language') || 'he');
    const [menuOpen, setMenuOpen] = useState(false);
    const rootRef = useRef(null);

    useEffect(() => {
        const currentLanguage = localStorage.getItem('language') || 'he';
        i18n.changeLanguage(currentLanguage);
    }, [i18n]);

    useEffect(() => {
        if (!menuOpen) return undefined;

        const onPointerDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) {
                setMenuOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') setMenuOpen(false);
        };

        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [menuOpen]);

    const changeLanguage = (language) => {
        i18n.changeLanguage(language);
        setSelectedLanguage(language);
        localStorage.setItem('language', language);
        if (onChange) onChange(language);
        setMenuOpen(false);
    };

    return (
        <div className={styles.languageSelector} ref={rootRef}>
            <button
                type="button"
                className={classNames(styles.trigger, { [styles.triggerOpen]: menuOpen })}
                onClick={() => setMenuOpen((open) => !open)}
                aria-haspopup="listbox"
                aria-expanded={menuOpen}
                aria-label={LANGUAGE_LABELS[selectedLanguage] || 'עברית'}
            >
                <svg className={styles.globe} width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
                    <path d="M3.5 12h17" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                    <path
                        d="M12 3c2.4 2.6 3.6 5.7 3.6 9s-1.2 6.4-3.6 9c-2.4-2.6-3.6-5.7-3.6-9s1.2-6.4 3.6-9Z"
                        stroke="currentColor"
                        strokeWidth="1.7"
                    />
                </svg>
                <span className={styles.label}>
                    {LANGUAGE_LABELS[selectedLanguage] || 'עברית'}
                </span>
                <svg
                    className={classNames(styles.chevron, { [styles.chevronOpen]: menuOpen })}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    aria-hidden="true"
                >
                    <path
                        d="M2.5 4.5 6 8l3.5-3.5"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </button>
            {menuOpen && (
                <div className={styles.menu} role="listbox">
                    {LANGUAGES.map((language) => (
                        <button
                            type="button"
                            key={language}
                            role="option"
                            aria-selected={selectedLanguage === language}
                            onClick={() => changeLanguage(language)}
                            className={classNames(styles.menuItem, {
                                [styles.selectedItem]: selectedLanguage === language,
                            })}
                        >
                            <span className={styles.langName}>{LANGUAGE_LABELS[language]}</span>
                            <span className={styles.langCode}>{language.toUpperCase()}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
